import React from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import {
  X,
  Sparkles,
  FileText,
  ClipboardList,
  Plus,
  AlertCircle,
} from "lucide-react";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  FormCard,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  ModalActions,
  SecondaryButton,
  PrimaryButton,
} from "../quotationMasterListShared";

interface NoDocumentModalProps {
  open: boolean;
  lead: any;
  type: "quotation" | "order";
  onClose: () => void;
  onConfirm: () => void;
}

export const NoDocumentModal: React.FC<NoDocumentModalProps> = ({
  open,
  lead,
  type,
  onClose,
  onConfirm,
}) => {
  if (!open || !lead) return null;

  const isQuotation = type === "quotation";
  const docName = isQuotation ? "quotation" : "order";
  const DocIcon = isQuotation ? FileText : ClipboardList;

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalShell style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <ModalHero>
          <HeroGlow />
          <HeroRow>
            <HeroText>
              <HeroBadge>
                <Sparkles size={11} />
                {isQuotation ? "Quotation share" : "Order share"}
              </HeroBadge>
              <ModalTitle>
                No {isQuotation ? "quotation" : "order"} found
              </ModalTitle>
              <ModalSubtitle>
                Create one from this lead to continue sharing on WhatsApp.
              </ModalSubtitle>
            </HeroText>
            <CloseBtn type="button" onClick={onClose} aria-label="Close">
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        <ModalBody>
          <FormCard>
            <EmptyIconWrap>
              <DocIcon size={28} strokeWidth={1.6} />
            </EmptyIconWrap>
            <EmptyTitle>Nothing to send yet</EmptyTitle>
            <EmptyDesc>
              We couldn&apos;t find an existing {docName} for{" "}
              <LeadName>{lead.name}</LeadName>
              {lead.phone ? (
                <>
                  {" "}
                  (<LeadPhone>{lead.phone}</LeadPhone>)
                </>
              ) : null}
              . Create a new {docName} with this lead&apos;s details to continue.
            </EmptyDesc>
            <HintRow>
              <AlertCircle size={14} />
              Lead contact details will be prefilled for you.
            </HintRow>
          </FormCard>
        </ModalBody>

        <ModalActions style={{ flexDirection: "row" }}>
          <SecondaryButton type="button" onClick={onClose} style={{ flex: 1 }}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="button" onClick={onConfirm} style={{ flex: 1.2 }}>
            <Plus size={16} />
            Create {isQuotation ? "Quotation" : "Order"}
          </PrimaryButton>
        </ModalActions>
      </ModalShell>
    </ModalOverlay>,
    document.body
  );
};

const EmptyIconWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 18px;
  margin: 4px auto 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c94daa;
  background: linear-gradient(145deg, #fff9f0 0%, #fdf0fa 100%);
  border: 1.5px solid rgba(201, 77, 170, 0.18);
  box-shadow: 0 8px 20px rgba(201, 77, 170, 0.1);
`;

const EmptyTitle = styled.h3`
  margin: 14px 0 0;
  text-align: center;
  font-family: Urbanist, sans-serif;
  font-size: 18px;
  font-weight: 800;
  color: #171717;
`;

const EmptyDesc = styled.p`
  margin: 8px 0 0;
  text-align: center;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.6;
  color: rgba(23, 23, 23, 0.58);
`;

const LeadName = styled.strong`
  color: #171717;
  font-weight: 700;
`;

const LeadPhone = styled.span`
  color: #c94daa;
  font-weight: 600;
`;

const HintRow = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 16px auto 0;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 201, 81, 0.12);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  svg {
    color: #f09a3e;
    flex-shrink: 0;
  }
`;
